import React, { useContext, useRef, useState } from 'react';
import { PaymentMethod } from '@stripe/stripe-js';

import { showSnackbar } from 'helpers/snackbar';

import { media } from 'context';
import { Icon, IconButton, Layout, Text } from 'ds';
import { deletePaymentMethod } from 'ds/StripePaymentMethodModal/requests';
import { useOnClickOutside } from 'ds/helpers';
import { actions } from 'store/Tenancy';
import { selectBuildingUnitTenanciesWithRent, selectServicesPaymentMethod } from 'store/Tenancy/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectTenancyId } from 'store/v2/User/selectors';

import TenantPaymentMethodCardOption from './TenantPaymentMethodCardOption';
import { updateTenancyPaymentMethodIds } from '../requests';

import { ReactComponent as StripeBank } from 'ds/StripeBank.svg';

interface TenantAdminPaymentMethodCardProps {
  paymentMethod: PaymentMethod;
  defaultServicesPaymentMethod?: boolean;
  services?: boolean;
  defaultRentPaymentMethod?: boolean;
  rentAndServicesPaymentMethod?: boolean;
  setSelectedPaymentMethodForCardMenu?: (paymentMethod: PaymentMethod) => void;
  setShowMobileTenantAdminPaymentCardMenu?: (show: boolean) => void;
}

const TenantAdminPaymentMethodCard: React.FC<TenantAdminPaymentMethodCardProps> = ({
  paymentMethod,
  defaultServicesPaymentMethod,
  services,
  defaultRentPaymentMethod,
  rentAndServicesPaymentMethod,
  setSelectedPaymentMethodForCardMenu,
  setShowMobileTenantAdminPaymentCardMenu
}) => {
  const dispatch = useAppDispatch();
  const tenancyId = useAppSelector(selectTenancyId);
  const buildingUnitTenanciesWithRent = useAppSelector(selectBuildingUnitTenanciesWithRent);
  const rentAvailable = buildingUnitTenanciesWithRent && buildingUnitTenanciesWithRent?.length > 0;
  const servicesPaymentMethod = useAppSelector(selectServicesPaymentMethod);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useContext(media);
  useOnClickOutside(containerRef, () => {
    setShowDropdown(false);
  });

  const handleRemovePaymentMethod = async () => {
    if (tenancyId == null || !paymentMethod.id) return;
    try {
      const paymentMethods = await deletePaymentMethod({ id: paymentMethod.id, tenancyId });
      showSnackbar({
        message: 'Payment method removed'
      });
      dispatch(actions.setPaymentMethods(paymentMethods.data));
    } catch (error) {
      console.error(error);
      showSnackbar({
        message: 'Failed to remove payment method',
        negative: true
      });
    }
  };

  const handleAssignToServicesAndSetAsDefault = async () => {
    if (!tenancyId) return;
    try {
      const response = await updateTenancyPaymentMethodIds({
        paymentMethodIdServices: paymentMethod.id,
        tenancyId
      });

      if (response.status === 200) {
        showSnackbar({
          message: rentAvailable ? 'Payment method assigned to services' : 'Payment method set as default'
        });
        dispatch(actions.setPaymentMethods(response.data));

        const servicesPaymentMethod = response.data.find(
          (paymentMethod: PaymentMethod) => paymentMethod.metadata.services_payment_method
        );

        dispatch(actions.setServicesPaymentMethod(servicesPaymentMethod));
      }
    } catch (error) {
      console.error(error);
      showSnackbar({
        message: 'Failed to update payment method',
        negative: true
      });
    }
  };

  const handleAssignToRent = async () => {
    if (!tenancyId) return;
    try {
      const response = await updateTenancyPaymentMethodIds({
        paymentMethodIdRent: paymentMethod.id,
        tenancyId
      });

      if (response.status === 200) {
        showSnackbar({
          message: 'Payment method assigned to rent'
        });
        dispatch(actions.setPaymentMethods(response.data));

        const rentPaymentMethod = response.data.find(
          (paymentMethod: PaymentMethod) => paymentMethod.metadata.rent_payment_method
        );

        dispatch(actions.setRentPaymentMethod(rentPaymentMethod));
      }
    } catch (error) {
      console.error(error);
      showSnackbar({
        message: 'Failed to update payment method',
        negative: true
      });
    }
  };

  if (isMobile) {
    return (
      <Layout
        width="100%"
        align="center"
        color="white"
        borderRadius={16}
        boxShadow="2px 0 22px 0 rgba(34.1176, 28.6275, 42.7451, 0.1200)"
        paddingTop={20}
        paddingBottom={16}
        paddingX={16}
        direction="column"
        gap={16}
      >
        <Layout width="100%" align="center" direction="row" justify="space-between">
          <Layout direction="row" gap={16} align="center">
            {paymentMethod.us_bank_account && <StripeBank />}
            {paymentMethod.card && (
              <Layout width={32} height={32} borderRadius={8} color="gray-50" align="center" justify="center">
                <Icon name="creditCard" size="md" color="gray-500" />
              </Layout>
            )}
            <Layout direction="row" gap={4} align="center">
              <Text size="body-sm">
                {paymentMethod.card?.brand
                  ? paymentMethod.card.brand.charAt(0).toUpperCase() + paymentMethod.card.brand.slice(1)
                  : paymentMethod.us_bank_account?.bank_name}
              </Text>
              <Text size="body-sm" wrap={false}>
                {paymentMethod.us_bank_account
                  ? `•••• ${paymentMethod.us_bank_account?.last4}`
                  : `•••• ${paymentMethod.card?.last4}`}
              </Text>
              {paymentMethod.card && (
                <Layout marginLeft={12}>
                  <Text size="body-sm">
                    {paymentMethod.card?.exp_month}/{paymentMethod.card?.exp_year}
                  </Text>
                </Layout>
              )}
            </Layout>
          </Layout>
          {rentAndServicesPaymentMethod && rentAvailable ? null : defaultServicesPaymentMethod ||
            (defaultRentPaymentMethod && rentAvailable) ? (
            !(defaultServicesPaymentMethod && servicesPaymentMethod && servicesPaymentMethod.card) &&
            !(defaultServicesPaymentMethod && !services) && (
              <IconButton
                name="ellipsisV"
                size="md"
                type="noBackground"
                iconColor="gray-400"
                onClick={() => {
                  setSelectedPaymentMethodForCardMenu?.(paymentMethod);
                  setShowMobileTenantAdminPaymentCardMenu?.(true);
                }}
              />
            )
          ) : (
            <IconButton
              name="ellipsisV"
              size="md"
              type="noBackground"
              iconColor="gray-400"
              onClick={() => {
                setSelectedPaymentMethodForCardMenu?.(paymentMethod);
                setShowMobileTenantAdminPaymentCardMenu?.(true);
              }}
            />
          )}
        </Layout>
        {((rentAndServicesPaymentMethod && rentAvailable) ||
          defaultServicesPaymentMethod ||
          (defaultRentPaymentMethod && rentAvailable)) && (
          <Layout width="100%" direction="row">
            {rentAndServicesPaymentMethod && rentAvailable && (
              <Layout direction="row" gap={8}>
                <Layout paddingX={10} paddingY={4} borderRadius={4} color="blue-100">
                  <Text size="body-xs" color="blue-600" semibold>
                    SERVICES
                  </Text>
                </Layout>
                <Layout paddingX={10} paddingY={4} borderRadius={4} color="blue-100">
                  <Text size="body-xs" color="blue-600" semibold>
                    RENT
                  </Text>
                </Layout>
              </Layout>
            )}
            {!(rentAndServicesPaymentMethod && rentAvailable) &&
              (defaultServicesPaymentMethod || (defaultRentPaymentMethod && rentAvailable)) && (
                <Layout paddingX={10} paddingY={4} borderRadius={4} color="blue-100" height="fit-content">
                  <Text size="body-xs" color="blue-600" semibold>
                    {defaultServicesPaymentMethod ? (services ? 'SERVICES' : 'DEFAULT') : 'RENT'}
                  </Text>
                </Layout>
              )}
          </Layout>
        )}
      </Layout>
    );
  }

  return (
    <Layout
      width="100%"
      height={80}
      paddingY={16}
      align="center"
      color="white"
      borderRadius={16}
      paddingX={24}
      position="relative"
      boxShadow="2px 0 22px 0 rgba(34.1176, 28.6275, 42.7451, 0.1200)"
    >
      <Layout width="100%" height={80} align="center" direction="row" justify="space-between">
        <Layout direction="row" gap={12} align="center">
          {paymentMethod.us_bank_account && <StripeBank />}
          {paymentMethod.card && (
            <Layout width={32} height={32} borderRadius={8} color="gray-50" align="center" justify="center">
              <Icon name="creditCard" size="md" color="gray-500" />
            </Layout>
          )}
          <Layout direction="row" gap={4} align="center">
            <Text size="body-sm">
              {paymentMethod.card?.brand
                ? paymentMethod.card.brand.charAt(0).toUpperCase() + paymentMethod.card.brand.slice(1)
                : paymentMethod.us_bank_account?.bank_name}
            </Text>
            <Text size="body-sm" wrap={false}>
              {paymentMethod.us_bank_account
                ? `•••• ${paymentMethod.us_bank_account?.last4}`
                : `•••• ${paymentMethod.card?.last4}`}
            </Text>
            {paymentMethod.card && (
              <Layout marginLeft={12}>
                <Text size="body-sm">
                  {paymentMethod.card?.exp_month}/{paymentMethod.card?.exp_year}
                </Text>
              </Layout>
            )}
          </Layout>
        </Layout>
        {rentAndServicesPaymentMethod && rentAvailable ? (
          <Layout direction="row" gap={8}>
            <Layout paddingX={10} paddingY={4} borderRadius={4} color="blue-100">
              <Text size="body-xs" color="blue-600" semibold>
                SERVICES
              </Text>
            </Layout>
            <Layout paddingX={10} paddingY={4} borderRadius={4} color="blue-100">
              <Text size="body-xs" color="blue-600" semibold>
                RENT
              </Text>
            </Layout>
          </Layout>
        ) : defaultServicesPaymentMethod || (defaultRentPaymentMethod && rentAvailable) ? (
          <Layout direction="row" gap={8} align="center">
            <Layout paddingX={10} paddingY={4} borderRadius={4} color="blue-100" height="fit-content">
              <Text size="body-xs" color="blue-600" semibold>
                {defaultServicesPaymentMethod ? (services ? 'SERVICES' : 'DEFAULT') : 'RENT'}
              </Text>
            </Layout>
            {!(defaultServicesPaymentMethod && servicesPaymentMethod && servicesPaymentMethod.card) &&
              !(defaultServicesPaymentMethod && !services) && (
                <IconButton
                  name="ellipsisV"
                  size="md"
                  type="noBackground"
                  iconColor="gray-400"
                  onClick={() => setShowDropdown(true)}
                />
              )}
          </Layout>
        ) : (
          <IconButton
            name="ellipsisV"
            size="md"
            type="noBackground"
            iconColor="gray-400"
            onClick={() => setShowDropdown(true)}
          />
        )}
      </Layout>

      {!isMobile && showDropdown && (
        <Layout
          zIndex={2}
          __ref={containerRef}
          width={246}
          position="absolute"
          top={68}
          right={0}
          color="white"
          borderRadius={8}
          boxShadow="2px 0 22px 0 rgba(34.1176, 28.6275, 42.7451, 0.1200)"
          paddingY={8}
        >
          {rentAvailable ? (
            <>
              {!defaultServicesPaymentMethod && (
                <TenantPaymentMethodCardOption
                  icon="featherDuster"
                  title="Assign to services"
                  onClick={handleAssignToServicesAndSetAsDefault}
                />
              )}
              {!paymentMethod.card &&
                !(defaultServicesPaymentMethod && servicesPaymentMethod?.card) &&
                !defaultRentPaymentMethod && (
                  <TenantPaymentMethodCardOption
                    icon="buildingNew"
                    title="Assign to rent"
                    onClick={handleAssignToRent}
                  />
                )}
            </>
          ) : (
            <TenantPaymentMethodCardOption
              icon="starOutline"
              title="Set as default"
              onClick={handleAssignToServicesAndSetAsDefault}
            />
          )}
          {!defaultServicesPaymentMethod && !defaultRentPaymentMethod && (
            <TenantPaymentMethodCardOption
              customIcon={
                <Layout
                  borderRadius="circular"
                  __style={{ borderWidth: 1.5 }}
                  borderStyle="solid"
                  display="inline-flex"
                  borderColor="gray-900"
                >
                  <Icon name="minus" size="sm" />
                </Layout>
              }
              title="Remove payment method"
              onClick={handleRemovePaymentMethod}
            />
          )}
        </Layout>
      )}
    </Layout>
  );
};

export default TenantAdminPaymentMethodCard;
